import type { BuildingLocation } from '../../dashboard/dashboard-buildings-components/building-location-picker';
import type { PropertyCreateInput, PropertyLayout, StaysProperty } from '../../../../api/stays-properties-client';
import { LAYOUT_WORDS } from '../../../../stays/wording';
import { can, PERMISSIONS } from '../../../../auth/permissions';

/**
 * The Add-a-property form's pure parts (Independent Stays IS2): what the form
 * holds, the body it sends, a pasted list of apartments, and the server's
 * field errors mapped back onto the form. The rules themselves are the
 * server's (BE src/stays/properties-input.js); this only shapes the request.
 */

export const MULTI_MIN = 2;
export const MULTI_MAX = 50;
export const LABEL_MAX = 24;
/** IS9 (R-IS9.1, BE properties-input.js): a hotel has 2-500 rooms in 1-20 room types. */
export const HOTEL_MIN = 2;
export const HOTEL_MAX = 500;
export const ROOM_TYPES_MAX = 20;
export const ROOM_TYPE_NAME_MAX = 40;

/** One room type of a new hotel: its name, how many it sleeps, its rooms as typed or pasted. */
export interface RoomTypeDraft {
  name: string;
  guests: number;
  rooms: string;
}

export const EMPTY_ROOM_TYPE: RoomTypeDraft = { name: '', guests: 2, rooms: '' };

export interface PropertyForm {
  layout: PropertyLayout | null;
  name: string;
  address: string;
  city: string;
  /** apartment: one; multi: one per row. */
  labels: string[];
  checkIn: string;
  checkOut: string;
  guests: number;
  attest: boolean;
  /** hotel only (IS9). */
  roomTypes: RoomTypeDraft[];
}

export const EMPTY_FORM: PropertyForm = {
  layout: null,
  name: '',
  address: '',
  city: '',
  labels: [''],
  checkIn: '15:00',
  checkOut: '11:00',
  guests: 2,
  attest: false,
  roomTypes: [{ ...EMPTY_ROOM_TYPE }],
};

/** The rows a layout shows: none for a whole place, one for an apartment, at least two for several. */
export const labelsFor = (layout: PropertyLayout | null, labels: string[]): string[] => {
  if (layout === 'whole' || layout === 'hotel' || !layout) return [];
  if (layout === 'apartment') return [labels[0] || ''];
  const rows = labels.length >= MULTI_MIN ? labels : [...labels, ...Array(MULTI_MIN - labels.length).fill('')];

  return rows.slice(0, MULTI_MAX);
};

/** "1A, 1B\n2A" → ['1A', '1B', '2A']: commas, semicolons, tabs and new lines separate; blanks dropped. */
export const splitPastedList = (text: string): string[] =>
  text
    .split(/[\n,;\t]+/)
    .map(s => s.trim())
    .filter(Boolean)
    .slice(0, MULTI_MAX);

/** A hotel's rooms: "101-104, 201" → ['101', '102', '103', '104', '201'] (a numeric range expands, up to the hotel's cap). */
export const splitRoomList = (text: string): string[] => {
  const out: string[] = [];
  for (const part of text.split(/[\n,;\t]+/).map(s => s.trim()).filter(Boolean)) {
    const range = /^(\d{1,5})\s*[-–]\s*(\d{1,5})$/.exec(part);
    if (range && Number(range[2]) >= Number(range[1]) && Number(range[2]) - Number(range[1]) < HOTEL_MAX) {
      for (let n = Number(range[1]); n <= Number(range[2]) && out.length < HOTEL_MAX; n++) out.push(String(n));
    } else if (out.length < HOTEL_MAX) out.push(part);
  }

  return out;
};

/** How many rooms the hotel form holds in all. */
export const hotelRoomCount = (types: RoomTypeDraft[]): number =>
  types.reduce((n, rt) => n + splitRoomList(rt.rooms).length, 0);

export const buildCreateInput = (
  form: PropertyForm,
  location: BuildingLocation,
  zone: string,
  attestationRequired: boolean
): PropertyCreateInput => {
  const layout = form.layout as PropertyLayout;
  const labels = labelsFor(layout, form.labels).map(l => l.trim());
  const loc: PropertyCreateInput['location'] = {};
  if (location.latitude !== null && location.longitude !== null) {
    loc.latitude = location.latitude;
    loc.longitude = location.longitude;
  }
  for (const k of ['country_code', 'country', 'formatted_address', 'place_id', 'place_provider', 'region', 'postal_code'] as const) {
    if (location[k]) loc[k] = location[k] as string;
  }
  const out: PropertyCreateInput = {
    layout,
    name: form.name.trim(),
    address: form.address.trim(),
    city: form.city.trim(),
    location: loc,
    listing_defaults: { check_in_time: form.checkIn, check_out_time: form.checkOut, max_guests: form.guests },
  };
  if (layout === 'hotel') {
    out.room_types = form.roomTypes.map(rt => ({
      name: rt.name.trim(),
      max_guests: rt.guests,
      check_in_time: form.checkIn,
      check_out_time: form.checkOut,
      rooms: splitRoomList(rt.rooms).map(label => ({ label })),
    }));
  } else if (layout !== 'whole') out.units = labels.map(label => ({ label }));
  if (attestationRequired) out.attest = form.attest;
  if (zone) out.timezone = zone;

  return out;
};

/** Which form field a server field key belongs to ('units.3' → the fourth apartment row). */
export const fieldOf = (key: string): string => {
  if (/^units\.\d+$/.test(key)) return key;
  // IS9: 'room_types.1.rooms.4' → the second type's room list; its name and guests stay.
  const rt = /^(room_types\.\d+)\.(rooms)(\.\d+)?$/.exec(key);
  if (rt) return `${rt[1]}.rooms`;
  if (/^room_types\.\d+\.(check_in_time|check_out_time)$/.test(key)) return 'check_in_time';
  if (key.startsWith('listing_defaults.')) return 'check_in_time';

  return key;
};

export const mapFieldErrors = (fields: Record<string, string>): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(fields || {})) {
    const f = fieldOf(k);
    if (!out[f]) out[f] = v;
  }

  return out;
};

/** Who may add, edit and remove a property — the server's guards, mirrored. */
export const propertyRights = (permissions: string[]): { canAdd: boolean; canEdit: boolean; canRemove: boolean } => {
  const promote = can(permissions, PERMISSIONS.STAYS_PROMOTE);

  return {
    canAdd: promote && can(permissions, PERMISSIONS.BUILDING_CREATE) && can(permissions, PERMISSIONS.UNIT_CREATE),
    canEdit: promote && can(permissions, PERMISSIONS.BUILDING_EDIT),
    canRemove: promote && can(permissions, PERMISSIONS.BUILDING_DELETE),
  };
};

/** What a property card says about its kind and its apartments. */
export const propertySummary = (p: StaysProperty): string => {
  const kind = LAYOUT_WORDS[p.layout].short;
  if (p.layout === 'hotel') {
    const n = p.units.length;
    const types = (p.room_types || []).length;

    return `${kind} · ${n} ${n === 1 ? 'room' : 'rooms'} in ${types} ${types === 1 ? 'room type' : 'room types'}`;
  }
  if (p.layout === 'multi') return `${kind} · ${p.units.length} ${p.units.length === 1 ? 'apartment' : 'apartments'}`;

  return kind;
};

/** The address line of a property card. */
export const addressLine = (p: StaysProperty): string =>
  [p.address, p.city].filter(Boolean).join(', ') || p.formatted_address || '';
